import { Row } from '@tanstack/react-table';
import {
  AutomatedReviewLog,
  AutomatedReviewLogAvailableAction,
} from 'models/automation/Reviews';
import { useTranslation } from 'react-i18next';
import Tooltip from 'components/tooltip/Tooltip';
import useActionCancelAutomatedReviewLog from '../../actions/cancel/useActionCancelAutomatedReviewLog';
import useActionRetryAutomatedReviewLog from '../../actions/retry/useActionRetryAutomatedReviewLog';
import useActionManualAutomatedReviewLog from '../../actions/manualReview/useActionManualAutomatedReviewLog';
import {
  ArrowCounterClockwiseIconStyled,
  PencilSquareIconStyled,
  XCircleFillIconStyled,
  ActionsCellContainer,
} from './ActionsCell.styles';

const ACTION_ICON_MAP = {
  [AutomatedReviewLogAvailableAction.RETRY]: (
    <ArrowCounterClockwiseIconStyled data-testid="action-icon-retry" />
  ),
  [AutomatedReviewLogAvailableAction.CANCEL]: (
    <XCircleFillIconStyled data-testid="action-icon-cancel" />
  ),
  [AutomatedReviewLogAvailableAction.MANUAL_REVIEW]: (
    <PencilSquareIconStyled data-testid="action-icon-manual-review" />
  ),
};

const ActionsCell = ({ row }: { row: Row<AutomatedReviewLog> }) => {
  const { t } = useTranslation();
  const { availableAction } = row.original;
  const { openCancelAutomatedReviewLogModal } =
    useActionCancelAutomatedReviewLog();
  const { retryAutomatedReviewLog, isRetrying } =
    useActionRetryAutomatedReviewLog();
  const { manualReviewAutomatedReviewLog } =
    useActionManualAutomatedReviewLog();

  const availableActionIcon = ACTION_ICON_MAP[availableAction];

  if (!availableActionIcon) {
    return null;
  }

  const handleAction = () => {
    switch (availableAction) {
      case AutomatedReviewLogAvailableAction.CANCEL:
        openCancelAutomatedReviewLogModal({ automatedReviewLog: row.original });
        break;
      case AutomatedReviewLogAvailableAction.RETRY:
        retryAutomatedReviewLog({ automatedReviewLog: row.original });
        break;
      case AutomatedReviewLogAvailableAction.MANUAL_REVIEW:
        manualReviewAutomatedReviewLog({ automatedReviewLog: row.original });
        break;
      default:
        break;
    }
  };

  return (
    <Tooltip
      id={`actions-${availableAction}-tooltip`}
      text={t(
        isRetrying
          ? 'pageAutomations.reviews.automatedReviewLogs.actions.retry.retrying'
          : 'pageAutomations.reviews.automatedReviewLogs.table.actions',
        {
          context: availableAction,
        },
      )}
    >
      <ActionsCellContainer onClick={handleAction} $isRetrying={isRetrying}>
        {availableActionIcon}
      </ActionsCellContainer>
    </Tooltip>
  );
};

export default ActionsCell;
